<template>
  <div class="md-layout text-center login-fix-page">
    <div class="md-layout-item md-size-100">
      <!-- Background decoration -->
    </div>
    <div
        class="md-layout-item md-size-33 md-medium-size-50 md-small-size-70 md-xsmall-size-100 login-page-fix"
    >
      <form @submit.prevent="passwordReset">
        <login-card header-color="green">
          <h4 slot="title" class="title">Reset Your Password</h4>
          <p slot="description" class="description">
            Enter your email and new password to reset
          </p>

          <!-- Email Field -->
          <md-field
              slot="inputs"
              class="form-group"
              :class="{'md-error': apiValidationErrors.email || emailError}"
              style="margin-bottom: 28px"
          >
            <md-icon>email</md-icon>
            <label>Email Address</label>
            <md-input
                v-model="reset.email"
                type="email"
                required
                @input="clearError('email')"
            />
            <validation-error :errors="apiValidationErrors.email" />
            <span class="md-error" v-if="emailError && !apiValidationErrors.email">
                            {{ emailError }}
                        </span>
          </md-field>

          <!-- New Password Field -->
          <md-field
              slot="inputs"
              class="form-group"
              :class="{'md-error': apiValidationErrors.password || passwordError}"
              style="margin-bottom: 28px"
          >
            <md-icon>lock_outline</md-icon>
            <label>New Password</label>
            <md-input
                v-model="reset.password"
                :type="showPassword ? 'text' : 'password'"
                required
                @input="onPasswordChange"
                @focus="showRequirements = true"
                @blur="showRequirements = false"
            />
            <md-button
                class="md-icon-button md-simple toggle-password"
                @click="showPassword = !showPassword"
                type="button"
            >
              <md-icon>{{ showPassword ? 'visibility_off' : 'visibility' }}</md-icon>
            </md-button>
            <validation-error :errors="apiValidationErrors.password" />
            <span class="md-error" v-if="passwordError && !apiValidationErrors.password">
                            {{ passwordError }}
                        </span>
          </md-field>

          <!-- Password Strength Meter -->
          <div slot="inputs" v-if="reset.password && reset.password.length > 0" class="password-meter">
            <div class="meter-bar">
              <div
                  class="meter-fill"
                  :style="{
                                    width: passwordScore + '%',
                                    background: passwordStrengthColor
                                }"
              ></div>
            </div>
            <div class="meter-label">
                            <span :style="{ color: passwordStrengthColor }">
                                <md-icon class="strength-icon">{{ passwordStrengthIcon }}</md-icon>
                                {{ passwordStrengthLabel }}
                            </span>
              <span class="score">{{ passwordScore }}%</span>
            </div>
          </div>

          <!-- Password Requirements -->
          <div slot="inputs" v-if="showRequirements || reset.password" class="password-requirements">
            <h5>Password Requirements:</h5>
            <ul>
              <li v-for="req in requirementsList" :key="req.key" :class="{'met': req.met}">
                <md-icon>{{ req.met ? 'check_circle' : 'radio_button_unchecked' }}</md-icon>
                {{ req.label }}
              </li>
            </ul>
          </div>

          <!-- Confirm Password Field -->
          <md-field
              slot="inputs"
              class="form-group"
              :class="{'md-error': apiValidationErrors.password_confirmation || passwordMismatch}"
              style="margin-bottom: 28px"
          >
            <md-icon>lock_outline</md-icon>
            <label>Confirm Password</label>
            <md-input
                v-model="reset.password_confirmation"
                :type="showPassword ? 'text' : 'password'"
                required
                @input="checkPasswordMatch"
            />
            <validation-error :errors="apiValidationErrors.password_confirmation" />
            <span class="md-error" v-if="passwordMismatch">
                            Passwords do not match
                        </span>
          </md-field>

          <!-- Token Field (Hidden) -->
          <input type="hidden" v-model="reset.token" />

          <!-- Password History Warning -->
          <div slot="inputs" v-if="passwordInHistory" class="history-warning">
            <md-icon>history</md-icon>
            <span>This password has been used recently. Please choose a different password.</span>
          </div>

          <!-- Success Message -->
          <div slot="inputs" v-if="successMessage" class="success-message">
            <md-icon>check_circle</md-icon>
            {{ successMessage }}
          </div>

          <!-- Error Message -->
          <div slot="inputs" v-if="generalError" class="error-message">
            <md-icon>error</md-icon>
            {{ generalError }}
          </div>

          <!-- Loading State -->
          <div slot="inputs" v-if="isLoading" class="loading-container">
            <md-progress-spinner md-mode="indeterminate" :md-diameter="diameter"></md-progress-spinner>
            <span>Resetting password...</span>
          </div>

          <!-- Submit Button -->
          <md-button
              slot="footer"
              class="md-simple md-success md-lg"
              type="submit"
              :disabled="isLoading || !isFormValid"
          >
            <md-icon>{{ isLoading ? 'hourglass_empty' : 'lock_reset' }}</md-icon>
            {{ isLoading ? 'Resetting...' : 'Reset Password' }}
          </md-button>

          <p slot="footer" class="login-links">
            <router-link to="/login">Back to Login</router-link>
          </p>
        </login-card>
      </form>
    </div>
  </div>
</template>

<script>
import { LoginCard, ValidationError } from '@/components';
import formMixin from '@/mixins/form-mixin';
import { mapActions } from 'vuex';
import debounce from 'lodash/debounce';

export default {
  name: 'ResetPasswordPage',
  components: {
    LoginCard,
    ValidationError,
  },
  mixins: [formMixin],
  computed: {
    requirementsList() {
      return [
        { key: 'length', label: 'At least 8 characters long', met: this.requirements.length },
        { key: 'uppercase', label: 'Contains at least one uppercase letter', met: this.requirements.uppercase },
        { key: 'lowercase', label: 'Contains at least one lowercase letter', met: this.requirements.lowercase },
        { key: 'number', label: 'Contains at least one number', met: this.requirements.number },
        { key: 'special', label: 'Contains at least one special character', met: this.requirements.special },
        { key: 'no_repeating', label: 'No three or more repeating characters', met: this.requirements.no_repeating },
        { key: 'no_common_pattern', label: 'Not a common or easily guessable password', met: this.requirements.no_common_pattern }
      ];
    },
    isFormValid() {
      if (!this.reset.email || !this.isValidEmail(this.reset.email)) return false;
      if (!this.reset.password || this.reset.password.length < 8) return false;
      if (!this.reset.password_confirmation) return false;
      if (this.passwordMismatch) return false;
      if (this.passwordInHistory) return false;
      if (this.passwordScore < 80) return false;
      return true;
    }
  },
  data() {
    return {
      diameter:30,
      reset: {
        email: '',
        password: '',
        password_confirmation: '',
        token: this.$route.query.token || ''
      },
      showPassword: false,
      showRequirements: false,
      isLoading: false,
      successMessage: '',
      generalError: '',
      emailError: '',
      passwordError: '',
      passwordMismatch: false,
      passwordInHistory: false,
      passwordScore: 0,
      passwordStrengthLabel: 'Weak',
      passwordStrengthColor: '#f44336',
      passwordStrengthIcon: 'error',
      requirements: {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
        no_repeating: false,
        no_common_pattern: false
      },
      isRedirecting: false
    };
  },
  created() {
    // Check if token exists in URL
    if (!this.reset.token) {
      this.generalError = 'Invalid or missing reset token. Please request a new password reset link.';
    }
  },
  methods: {
    ...mapActions('passwordModule', ['noneLoginResetPassword', 'checkPasswordStrength']),

    onPasswordChange: debounce(function() {
      this.checkPasswordStrengthDebounced();
    }, 300),

    async checkPasswordStrengthDebounced() {
      if (!this.reset.password || this.reset.password.length < 1) {
        return;
      }

      try {
        const response = await this.checkPasswordStrength({
          password: this.reset.password
        });

        const data = response.data || response;
        this.passwordScore = data.score || 0;
        this.passwordStrengthLabel = data.strength ? data.strength.label : 'Weak';
        this.passwordStrengthColor = data.strength ? data.strength.color : '#f44336';
        this.passwordStrengthIcon = this.getStrengthIcon(this.passwordScore);
        this.requirements = data.requirements || {};
        this.passwordInHistory = false;
        this.passwordError = '';
      } catch (error) {
        console.error('Password strength check failed:', error);
        // If check fails, use default values
        this.passwordScore = this.calculateBasicStrength(this.reset.password);
        this.passwordStrengthLabel = this.getStrengthLabel(this.passwordScore);
        this.passwordStrengthColor = this.getStrengthColor(this.passwordScore);
        this.passwordStrengthIcon = this.getStrengthIcon(this.passwordScore);
      }
    },

    calculateBasicStrength(password) {
      let score = 0;
      if (password.length >= 8) score += 20;
      if (password.length >= 12) score += 10;
      if (/[a-z]/.test(password)) score += 10;
      if (/[A-Z]/.test(password)) score += 10;
      if (/[0-9]/.test(password)) score += 15;
      if (/[^A-Za-z0-9]/.test(password)) score += 20;
      if (!/(.)\1{2,}/.test(password)) score += 15;
      return Math.min(score, 100);
    },

    getStrengthLabel(score) {
      if (score < 40) return 'Weak';
      if (score < 60) return 'Fair';
      if (score < 80) return 'Good';
      return 'Strong';
    },

    getStrengthColor(score) {
      if (score < 40) return '#f44336';
      if (score < 60) return '#ff9800';
      if (score < 80) return '#2196f3';
      return '#4caf50';
    },

    getStrengthIcon(score) {
      if (score < 40) return 'error';
      if (score < 60) return 'warning';
      if (score < 80) return 'info';
      return 'check_circle';
    },

    checkPasswordMatch() {
      if (this.reset.password_confirmation && this.reset.password) {
        this.passwordMismatch = this.reset.password !== this.reset.password_confirmation;
      } else {
        this.passwordMismatch = false;
      }
    },

    isValidEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },

    clearError(field) {
      if (field === 'email') this.emailError = '';
      if (field === 'password') this.passwordError = '';
      this.resetApiValidation();
    },

    async passwordReset() {
      // Prevent multiple submissions
      if (this.isLoading || this.isRedirecting) return;

      // Reset states
      this.successMessage = '';
      this.generalError = '';
      this.emailError = '';
      this.passwordError = '';
      this.resetApiValidation();

      // Validate email
      if (!this.reset.email || !this.isValidEmail(this.reset.email)) {
        this.emailError = 'Please enter a valid email address';
        return;
      }

      // Validate password
      if (!this.reset.password || this.reset.password.length < 8) {
        this.passwordError = 'Password must be at least 8 characters long';
        return;
      }

      // Validate confirm password
      if (this.reset.password !== this.reset.password_confirmation) {
        this.passwordMismatch = true;
        return;
      }

      // Validate password strength
      if (this.passwordScore < 80) {
        this.passwordError = 'Please choose a stronger password';
        return;
      }

      this.isLoading = true;

      try {
        const payload = {
          email: this.reset.email,
          password: this.reset.password,
          password_confirmation: this.reset.password_confirmation,
          token: this.reset.token
        };

        await this.noneLoginResetPassword(payload);

        this.successMessage = 'Password reset successfully! Redirecting to login...';

        // Show success alert
        this.$store.dispatch('alerts/success', 'Password reset successfully!');

        // Set redirecting flag to prevent loops
        this.isRedirecting = true;

        // Redirect to login after 2 seconds
        setTimeout(() => {
          this.isRedirecting = false;
          this.$router.push('/login');
        }, 4000);

      } catch (error) {
        console.error('Password reset error:', error);

        if (error.response && error.response.data) {
          if (error.response.data.errors) {
            const errors = error.response.data.errors;
            if (Array.isArray(errors)) {
              errors.forEach(err => {
                if (err.source && err.source.pointer) {
                  const field = err.source.pointer.split('/').pop();
                  if (field === 'email') {
                    this.emailError = err.detail;
                  } else if (field === 'password') {
                    this.passwordError = err.detail;
                  } else {
                    this.generalError = err.detail;
                  }
                } else {
                  this.generalError = err.detail || 'Failed to reset password';
                }
              });
            } else {
              this.generalError = error.response.data.message || 'Failed to reset password';
            }
          } else if (error.response.data.message) {
            this.generalError = error.response.data.message;
          } else {
            this.generalError = 'Failed to reset password. Please try again.';
          }
        } else {
          this.generalError = 'Network error. Please check your connection.';
        }

        // Show error alert
        this.$store.dispatch('alerts/error', this.generalError);

      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.login-fix-page {
  padding-bottom: 7em;
  padding-top: 4em;
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.login-page-fix {
  margin: 0 auto;
}

.title {
  font-weight: 600;
  color: #2d3748;
}

.description {
  color: #718096;
  font-size: 14px;
  margin-top: 5px;
}

/* Toggle Password */
.toggle-password {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.toggle-password .md-icon {
  font-size: 20px;
}

/* Password Meter */
.password-meter {
  margin: 10px 0 15px;
  padding: 0 20px;
}

.meter-bar {
  width: 100%;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.meter-fill {
  height: 100%;
  transition: width 0.3s ease;
}

.meter-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 5px;
  font-size: 13px;
}

.meter-label .strength-icon {
  font-size: 18px;
  width: 18px;
  height: 18px;
  vertical-align: middle;
}

.score {
  color: #999;
  font-weight: 500;
}

/* Password Requirements */
.password-requirements {
  background: #f5f5f5;
  padding: 15px 20px;
  border-radius: 6px;
  margin: 10px 0 15px;
}

.password-requirements h5 {
  margin: 0 0 8px 0;
  font-size: 13px;
  color: #555;
}

.password-requirements ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.password-requirements li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 0;
  font-size: 12px;
  color: #999;
  transition: color 0.2s;
}

.password-requirements li.met {
  color: #4caf50;
}

.password-requirements li md-icon {
  font-size: 18px;
  width: 18px;
  height: 18px;
}

/* History Warning */
.history-warning {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  background: #fff3e0;
  border-radius: 4px;
  margin: 10px 0;
  color: #e65100;
  font-size: 13px;
}

.history-warning md-icon {
  color: #e65100;
}

/* Success Message */
.success-message {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 15px;
  background: #e8f5e9;
  border-radius: 4px;
  color: #2e7d32;
  margin: 10px 0;
}

.success-message md-icon {
  color: #2e7d32;
}

/* Error Message */
.error-message {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 15px;
  background: #ffebee;
  border-radius: 4px;
  color: #c62828;
  margin: 10px 0;
}

.error-message md-icon {
  color: #c62828;
}

/* Loading Container */
.loading-container {
  display: flex;
  align-items: center;
  gap: 15px;
  justify-content: center;
  padding: 20px 0;
  color: #666;
}

/* Login Links */
.login-links {
  margin-top: 15px;
  text-align: center;
}

.login-links a {
  color: #4caf50;
  text-decoration: none;
  font-size: 14px;
}

.login-links a:hover {
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 768px) {
  .login-fix-page {
    padding-bottom: 3em;
    padding-top: 2em;
  }

  .password-requirements {
    padding: 10px 15px;
  }
}
</style>